// Base chain configuration for Limitless Exchange
export const BASE_CHAIN_ID = 8453
export const BASE_RPC_URL =
	process.env.NEXT_PUBLIC_BASE_RPC_URL ||
	`https://base-mainnet.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`

// USDC on Base (native, not bridged)
export const BASE_USDC_ADDRESS = '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913' as const
export const BASE_USDC_DECIMALS = 6

// Limitless API
export const LIMITLESS_API_URL = 'https://api.limitless.exchange'
export const LIMITLESS_WS_URL = 'wss://ws.limitless.exchange'

// EIP-712 domain (verifyingContract comes from market venue)
export const LIMITLESS_EIP712_DOMAIN_NAME = 'Limitless CTF Exchange'
export const LIMITLESS_EIP712_DOMAIN_VERSION = '1'

// NegRisk CTF Exchange → NegRisk Adapter on Base.
// Limitless NegRisk orders settle via the adapter, so CTF needs setApprovalForAll
// on BOTH the NegRisk exchange AND its adapter for sells to fill.
// Docs: https://docs.limitless.exchange/user-guide/smart-contracts
export const LIMITLESS_NEGRISK_EXCHANGE_TO_ADAPTER: Record<string, `0x${string}`> = {
	'0x5a38afc17f7e97ad8d6c547ddb837e40b4aedfc6': '0xb8DAA4C8C9f690396f671BB601727A4c3741340C',
	'0x46e607d3f4a8494b0ab9b304d1463e2f4848891d': '0x7afeB946986211950d17f24176039F12c2aB2436',
	'0xe3e00ba3a9888d1de4834269f62ac008b4bb5c47': '0x6151EF8368b6316c1aa3C68453EF083ad31E712D',
}

export function getLimitlessNegRiskAdapter(exchange: string): `0x${string}` | undefined {
	return LIMITLESS_NEGRISK_EXCHANGE_TO_ADAPTER[exchange.toLowerCase()]
}

// Conditional Tokens Framework on Base (ERC1155)
export const BASE_CTF_ADDRESS = '0xC9c98965297Bc527861c898329Ee280632B76e18' as const

// Minimal ERC1155 ABI for CTF approval + redemption
export const CTF_ABI = [
	{
		type: 'function',
		name: 'redeemPositions',
		inputs: [
			{ name: 'collateralToken', type: 'address' },
			{ name: 'parentCollectionId', type: 'bytes32' },
			{ name: 'conditionId', type: 'bytes32' },
			{ name: 'indexSets', type: 'uint256[]' },
		],
		outputs: [],
		stateMutability: 'nonpayable',
	},
	{
		type: 'function',
		name: 'setApprovalForAll',
		inputs: [
			{ name: 'operator', type: 'address' },
			{ name: 'approved', type: 'bool' },
		],
		outputs: [],
		stateMutability: 'nonpayable',
	},
	{
		type: 'function',
		name: 'isApprovedForAll',
		inputs: [
			{ name: 'account', type: 'address' },
			{ name: 'operator', type: 'address' },
		],
		outputs: [{ name: '', type: 'bool' }],
		stateMutability: 'view',
	},
	{
		type: 'function',
		name: 'balanceOf',
		inputs: [
			{ name: 'account', type: 'address' },
			{ name: 'id', type: 'uint256' },
		],
		outputs: [{ name: '', type: 'uint256' }],
		stateMutability: 'view',
	},
] as const

// Gnosis FixedProductMarketMaker ABI (subset used for AMM trading)
export const FPMM_ABI = [
	{
		type: 'function',
		name: 'buy',
		inputs: [
			{ name: 'investmentAmount', type: 'uint256' },
			{ name: 'outcomeIndex', type: 'uint256' },
			{ name: 'minOutcomeTokensToBuy', type: 'uint256' },
		],
		outputs: [],
		stateMutability: 'nonpayable',
	},
	{
		type: 'function',
		name: 'sell',
		inputs: [
			{ name: 'returnAmount', type: 'uint256' },
			{ name: 'outcomeIndex', type: 'uint256' },
			{ name: 'maxOutcomeTokensToSell', type: 'uint256' },
		],
		outputs: [],
		stateMutability: 'nonpayable',
	},
	{
		type: 'function',
		name: 'calcBuyAmount',
		inputs: [
			{ name: 'investmentAmount', type: 'uint256' },
			{ name: 'outcomeIndex', type: 'uint256' },
		],
		outputs: [{ name: '', type: 'uint256' }],
		stateMutability: 'view',
	},
	{
		type: 'function',
		name: 'calcSellAmount',
		inputs: [
			{ name: 'returnAmount', type: 'uint256' },
			{ name: 'outcomeIndex', type: 'uint256' },
		],
		outputs: [{ name: '', type: 'uint256' }],
		stateMutability: 'view',
	},
	{
		type: 'function',
		name: 'conditionalTokens',
		inputs: [],
		outputs: [{ name: '', type: 'address' }],
		stateMutability: 'view',
	},
	{
		type: 'function',
		name: 'collateralToken',
		inputs: [],
		outputs: [{ name: '', type: 'address' }],
		stateMutability: 'view',
	},
	{
		type: 'function',
		name: 'fee',
		inputs: [],
		outputs: [{ name: '', type: 'uint256' }],
		stateMutability: 'view',
	},
	{
		type: 'function',
		name: 'conditionIds',
		inputs: [{ name: '', type: 'uint256' }],
		outputs: [{ name: '', type: 'bytes32' }],
		stateMutability: 'view',
	},
] as const
